/**
 * useContactDetails Hook
 *
 * Handles contact details for the right panel, including stats and activities.
 * Uses RTK Query for API calls and mappers to transform data.
 */

import {useState, useEffect, useMemo, useCallback} from 'react'
import {useLazyGetContactInformationQuery} from '../../../../../services/cellcast.v2'
import {useLazyGetConversationByIdQuery} from '../../../../../services/cellcast'
import {ConversationData, ContactDetails, ContactStats} from '../types/inbox-v3.types'
import {extractContactFromConversation, mapContactInformationToV3} from '../services/inboxMapper'

// ============================================================================
// Types
// ============================================================================

interface UseContactDetailsOptions {
  /** The conversation to get contact details for */
  conversation: ConversationData | null
  /** Whether to automatically fetch when conversation changes (default: true) */
  autoFetch?: boolean
}

interface UseContactDetailsReturn {
  // Data
  contactDetails: ContactDetails | null
  contactStats: ContactStats | null
  isLoading: boolean
  isLoadingStats: boolean
  error: string | null

  // Computed
  displayName: string
  initials: string

  // Actions
  refetch: () => Promise<void>
}

// Default empty stats
const defaultStats: ContactStats = {
  total: 0,
  inbound: 0,
  outbound: 0,
  unread: 0,
  bulk: 0,
  quick: 0,
  sms: 0,
  mms: 0,
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useContactDetails(options: UseContactDetailsOptions): UseContactDetailsReturn {
  const {conversation, autoFetch = true} = options

  // State
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(null)
  const [contactStats, setContactStats] = useState<ContactStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingStats, setIsLoadingStats] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // RTK Query hooks
  const [fetchContactInfo] = useLazyGetContactInformationQuery()
  const [fetchConversationById] = useLazyGetConversationByIdQuery()

  // ============================================================================
  // Fetch Functions
  // ============================================================================

  /**
   * Fetch contact details from API
   */
  const fetchContactDetails = useCallback(async () => {
    if (!conversation) {
      setContactDetails(null)
      return
    }

    // Use participantRefId for new API conversations, fallback to contactgroupmaps._id for legacy
    const contactId = conversation.participantRefId || conversation.contactgroupmaps?._id

    // If no contact ID, extract from conversation data directly
    if (!contactId) {
      const extracted = extractContactFromConversation(conversation)
      setContactDetails(extracted)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Fetch contact information
      const contactResponse = await fetchContactInfo(contactId).unwrap()

      if (contactResponse?.data) {
        const mappedContact = mapContactInformationToV3(contactResponse.data, conversation.receiver)
        setContactDetails(mappedContact)
      } else {
        // Fallback to extracting from conversation
        const extracted = extractContactFromConversation(conversation)
        setContactDetails(extracted)
      }
    } catch (err: any) {
      console.error('Error fetching contact details:', err)
      // Fallback to extracting from conversation on error
      const extracted = extractContactFromConversation(conversation)
      setContactDetails(extracted)
      setError(err?.message || 'Failed to fetch contact details')
    } finally {
      setIsLoading(false)
    }
  }, [conversation, fetchContactInfo])

  /**
   * Fetch message stats from conversation API
   */
  const fetchStats = useCallback(async () => {
    if (!conversation) {
      setContactStats(null)
      return
    }

    // Use conversation _id to fetch stats from conversation API
    const conversationId = conversation._id

    if (!conversationId) {
      setContactStats(defaultStats)
      return
    }

    setIsLoadingStats(true)

    try {
      // Fetch conversation details which includes messageCount and messageTypeCount
      const response = await fetchConversationById({id: conversationId}).unwrap()

      if (response?.data) {
        const {messageCount, messageTypeCount} = response.data

        // Map to ContactStats
        setContactStats({
          total: messageCount?.total || 0,
          inbound: messageCount?.inbound || 0,
          outbound: messageCount?.outbound || 0,
          unread: messageCount?.unread || 0,
          bulk: messageCount?.bulk || 0,
          quick: messageCount?.quick || 0,
          sms: messageTypeCount?.sms || 0,
          mms: messageTypeCount?.mms || 0,
        })
      } else {
        setContactStats(defaultStats)
      }
    } catch (err: any) {
      console.error('Error fetching contact stats:', err)
      setContactStats(defaultStats)
    } finally {
      setIsLoadingStats(false)
    }
  }, [conversation, fetchConversationById])

  /**
   * Refetch all contact data
   */
  const refetch = useCallback(async () => {
    await Promise.all([fetchContactDetails(), fetchStats()])
  }, [fetchContactDetails, fetchStats])

  // ============================================================================
  // Effects
  // ============================================================================

  // Fetch contact details and stats when conversation changes
  useEffect(() => {
    if (autoFetch) {
      fetchContactDetails()
      fetchStats()
    }
  }, [autoFetch, fetchContactDetails, fetchStats])

  // ============================================================================
  // Computed Values
  // ============================================================================

  // Display name (full name or phone number)
  const displayName = useMemo(() => {
    if (!conversation) return ''

    // Use contact details if available
    if (contactDetails?.fullName) {
      return contactDetails.fullName
    }

    // Fallback to extracting from conversation
    const fields = conversation.contactgroupmaps?.fields || []
    const firstName = fields.find((f) => f.name?.toLowerCase() === 'first name')?.value || ''
    const lastName = fields.find((f) => f.name?.toLowerCase() === 'last name')?.value || ''
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || conversation.receiver
  }, [conversation, contactDetails])

  // Initials for avatar
  const initials = useMemo(() => {
    if (!conversation) return ''

    // Use contact details if available
    if (contactDetails?.firstName || contactDetails?.lastName) {
      return `${contactDetails.firstName?.charAt(0) || ''}${
        contactDetails.lastName?.charAt(0) || ''
      }`.toUpperCase()
    }

    // Fallback to extracting from conversation
    const fields = conversation.contactgroupmaps?.fields || []
    const firstName = fields.find((f) => f.name?.toLowerCase() === 'first name')?.value || ''
    const lastName = fields.find((f) => f.name?.toLowerCase() === 'last name')?.value || ''

    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    }

    // Use last 2 digits of phone number
    const phone = conversation.receiver
    return phone.slice(-2)
  }, [conversation, contactDetails])

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // Data
    contactDetails,
    contactStats,
    isLoading,
    isLoadingStats,
    error,

    // Computed
    displayName,
    initials,

    // Actions
    refetch,
  }
}